import { Contract } from "@ravalmatic/bridge-engine/core";
import { NineSliceContainer } from "@ravalmatic/catafractum-utils";

export class ContractBid extends Phaser.GameObjects.Container {

    private static readonly BORDER_SIZE = 48;
    private static readonly SUIT_FRAMES = [
        "clubs_icon",
        "diamonds_icon",
        "hearts_icon",
        "spades_icon"
    ];
    private background: NineSliceContainer;
    private backgroundFrame = "contract_bid_bg";
    private readonly badgeWidth: number;
    private readonly badgeHeight: number;

    constructor(scene: Phaser.Scene, contract: Contract, size?: { width: number; height: number }) {

        super(scene);

        const { level, strain, doubled, redoubled } = contract;
        const doubling = redoubled ? "XX" : doubled ? "X" : "";
        const frame = scene.textures.getFrame("texture_atlas_1", "contract_bid_bg");
        if (!frame) {
            throw new Error("Missing atlas frame: contract_bid_bg");
        }
        const width = size?.width ?? frame.width;
        const height = size?.height ?? frame.height;
        this.badgeWidth = width;
        this.badgeHeight = height;
        this.background = this.createBackground();
        const color = strain === 1 || strain === 2 ? "#D92727" : "#1F252D";
        const levelLabel = new Phaser.GameObjects.Text(scene, 0, 0, `${level}`, {
            color,
            fontFamily: "Baloo2-SemiBold",
            fontSize: "72px"
        }).setOrigin(.5);
        const content: (Phaser.GameObjects.Text | Phaser.GameObjects.Image)[] = [levelLabel];

        if (strain === 4) {
            const noTrumpLabel = new Phaser.GameObjects.Text(scene, 0, 0, "NT", {
                color,
                fontFamily: "Baloo2-SemiBold",
                fontSize: "54px"
            }).setOrigin(.5);
            content.push(noTrumpLabel);
        } else {
            const suitIcon = new Phaser.GameObjects.Image(
                scene, 0, 0, "texture_atlas_1", ContractBid.SUIT_FRAMES[strain]
            );
            suitIcon.setScale(.4);
            content.push(suitIcon);
        }

        if (doubling) {
            const doublingLabel = new Phaser.GameObjects.Text(scene, 0, 0, doubling, {
                color: "#ed171b",
                fontFamily: "Baloo2-SemiBold",
                fontSize: "64px"
            }).setOrigin(.5);
            content.push(doublingLabel);
        }

        if (content.length >= 3) {
            content.forEach(item => {
                if (item instanceof Phaser.GameObjects.Text) {
                    item.setScale(.85, 1);
                }
            });
        }

        const spacing = 4;
        const contentWidth = content.reduce((width, item) => width + item.displayWidth, 0)
            + spacing * (content.length - 1);
        let x = -contentWidth / 2;
        content.forEach(item => {
            item.setX(x + item.displayWidth / 2);
            x += item.displayWidth + spacing;
        });

        const row = new Phaser.GameObjects.Container(scene, 0, -4, content);
        const heightScale = size
            ? (size.height - 16) / Math.max(...content.map(item => item.displayHeight))
            : 1;
        row.setScale(Math.min(1, (width - 24) / contentWidth, heightScale));
        this.add([this.background, row]);
    }

    public setOutcome(outcome: "won" | "failed" | null): void {

        const frame = outcome ? `contract_bid_${outcome}_bg` : "contract_bid_bg";
        if (frame === this.backgroundFrame) {
            return;
        }
        const background = this.createBackground(frame);
        this.background.destroy();
        this.background = background;
        this.backgroundFrame = frame;
        this.addAt(background, 0);
    }

    private createBackground(frameName = "contract_bid_bg"): NineSliceContainer {

        const frame = this.scene.textures.getFrame("texture_atlas_1", frameName);
        if (!frame) {
            throw new Error(`Missing atlas frame: ${frameName}`);
        }
        const scale = Math.min(1, this.badgeWidth / frame.width, this.badgeHeight / frame.height);
        const background = new NineSliceContainer(
            this.scene, 0, 0, "texture_atlas_1", frameName,
            this.badgeWidth / scale, this.badgeHeight / scale, ContractBid.BORDER_SIZE
        );
        return background.setScale(scale);
    }
}
